/**
 * Client-side platform detection for the A1 badge and the inline E1.
 *
 * The patterns come from `GET /api/platforms`, never from a list in this
 * bundle. contracts/api.md §3.7 is explicit about why: a hardcoded frontend
 * list drifts from the server's, and the drift shows up as a badge that
 * promises a caption path the server no longer has. The same endpoint carries
 * live `health`, which cannot be baked into a 24 h-cached page — a platform
 * degraded at 09:00 would still advertise itself as working at 08:59 the next
 * morning.
 *
 * Detection is deliberately CONSERVATIVE. E1 is raised before submission only
 * for a host the server has explicitly named unsupported, or named as a podcast
 * app whose audio cannot be fetched (unless the link is one of the app's
 * episode links the server lists as working: a Spotify episode is badged
 * "Spotify" and goes through). An unrecognised host
 * gets no badge and no error, and the submission goes through to be judged
 * server-side — because the sixth platform is podcast/RSS, whose hosts are
 * every hostname on the internet, and a client that refuses what it does not
 * recognise would refuse all of them.
 */

import { getPlatforms } from './api.ts';
import type { PlatformInfo, PlatformsResponse } from './api-types.ts';

let cache: PlatformsResponse | null = null;
let inFlight: Promise<PlatformsResponse | null> | null = null;

export function cachedPlatforms(): PlatformsResponse | null {
  return cache;
}

/** Never rejects. A page without platform data degrades to no badge. */
export function loadPlatforms(): Promise<PlatformsResponse | null> {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = getPlatforms()
      .then((data) => {
        cache = data;
        return data;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

function parse(raw: string): URL | null {
  const text = raw.trim();
  if (text === '') return null;
  // A pasted URL routinely arrives without a scheme. Adding one for parsing is
  // not the same as accepting it: the server normalizes the real thing.
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`;
  try {
    return new URL(candidate);
  } catch {
    return null;
  }
}

/** The server's pattern, or null for one this browser cannot compile. */
function pattern(source: string): RegExp | null {
  try {
    return new RegExp(source);
  } catch {
    return null;
  }
}

function matches(host: string, pattern: string): boolean {
  const p = pattern.toLowerCase().replace(/^www\./, '');
  return host === p || host.endsWith(`.${p}`);
}

/**
 * `label` is the badge: the platform's own label, or an episode link's app
 * ("Spotify") when the link is one, transcribed by `platform`.
 */
export type Detection =
  | { readonly kind: 'platform'; readonly platform: PlatformInfo; readonly label: string }
  | { readonly kind: 'podcast_app'; readonly host: string; readonly app: string }
  | { readonly kind: 'unsupported'; readonly host: string }
  | { readonly kind: 'unknown'; readonly host: string | null };

export function detect(url: string, data: PlatformsResponse | null = cache): Detection {
  const parsed = parse(url);
  const host = parsed ? parsed.hostname.toLowerCase().replace(/^www\./, '') : null;
  if (!parsed || !host || !data) return { kind: 'unknown', host };

  for (const platform of data.platforms) {
    for (const p of platform.url_patterns) {
      if (matches(host, p)) return { kind: 'platform', platform, label: platform.label };
    }
  }
  // Before `podcast_apps`: an episode link into a podcast app works, though
  // the app's host is listed there for every other link to it.
  for (const link of data.episode_links ?? []) {
    if (host !== link.host.toLowerCase().replace(/^www\./, '')) continue;
    if (!pattern(link.path)?.test(parsed.pathname)) continue;
    const platform = data.platforms.find((p) => p.platform === link.platform);
    if (platform) return { kind: 'platform', platform, label: link.label };
  }
  // Before `unsupported_hosts`: a podcast app may be listed there too, and
  // its own E1 says what to paste instead, which the plain one cannot.
  for (const [pattern, app] of Object.entries(data.podcast_apps ?? {})) {
    if (matches(host, pattern)) return { kind: 'podcast_app', host, app };
  }
  for (const bad of data.unsupported_hosts) {
    if (matches(host, bad)) return { kind: 'unsupported', host };
  }
  return { kind: 'unknown', host };
}

export function labelFor(machineName: string, data: PlatformsResponse | null = cache): string {
  const found = data?.platforms.find((p) => p.platform === machineName);
  return found?.label ?? machineName;
}

/**
 * The platform page's H1 noun ("X video"), or '' when the platform data has
 * not arrived. Unlike `labelFor` there is no fallback to the machine name:
 * "a single twitter" is worse than the plain E1 the caller falls back to.
 */
export function nounFor(machineName: string, data: PlatformsResponse | null = cache): string {
  return data?.platforms.find((p) => p.platform === machineName)?.noun ?? '';
}
